import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Page } from "../components/Page";
import { ApiError, api } from "../lib/api";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What each refusal says, by the API's code. */
const MESSAGES: Record<string, string> = {
  code_not_found: "We don't recognise that code. Check it against where you got it: it looks like GP-7KQ4-M2XD-9HTR.",
  code_taken: "This code is already registered to a different email. Use the email you registered it with.",
  code_revoked: "This code can't be used. If you think that's wrong, email support@novosnetwork.com.",
  code_test_started: "You've already started the free test from this code. Sign in with your email to pick it up.",
  email_mismatch: "The two emails don't match.",
  bad_email: "Enter your email address.",
};

/**
 * /redeem (giftsprofile.com/redeem comes here): a code for a free test (from a book, or sent by
 * hand) and an email. The first time, that locks the code to the email for good, then the start
 * form opens with the email filled in, for one free test (Edward, Oct 26). The same code and email
 * again picks it up. /redeem#c=CODE fills the code in (in the hash, so it never reaches a server
 * log or a Referer), for a link in an email.
 */
export function RedeemPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const ids = { code: useId(), email: useId(), confirm: useId(), note: useId() };
  const [code, setCode] = useState(() => new URLSearchParams(location.hash.replace(/^#/, "")).get("c") ?? "");
  // The code is in the box now: out of the address bar.
  useEffect(() => {
    if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [used, setUsed] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setUsed(false);
    if (!code.trim()) return setError("Enter the code from your book.");
    if (!EMAIL.test(email.trim())) return setError("Enter your email address.");
    if (confirm.trim().toLowerCase() !== email.trim().toLowerCase()) return setError("The two emails don't match.");
    setBusy(true);
    try {
      const { url } = await api.redeemCode(code.trim(), email.trim(), confirm.trim());
      // /i#t=...: the start form, with the email filled in and fixed.
      const [pathname, hash] = url.split("#");
      navigate({ pathname, hash: hash ? `#${hash}` : "" });
    } catch (err) {
      if (err instanceof ApiError && err.code === "code_used") setUsed(true);
      else if (err instanceof ApiError && err.status === 429) setError("Too many wrong tries, so this is paused for a while. Check the code and try again later.");
      else setError(err instanceof ApiError ? (MESSAGES[err.code] ?? "Something went wrong. Please try again.") : "Something went wrong. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <h1 className="heading mt-10 text-[30px] leading-[1.1]">Redeem your code</h1>
      <p className="mt-3 text-pretty text-[15px] text-muted">Your code is for a free Gifts Profile. Enter it with your email to take it.</p>
      <form className="mt-6 flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.code} className="text-[15px] font-semibold">
            Your code
          </label>
          <input
            id={ids.code}
            name="code"
            className="field-input uppercase tracking-[0.06em]"
            placeholder="GP-7KQ4-M2XD-9HTR"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.email} className="text-[15px] font-semibold">
            Email
          </label>
          <input
            id={ids.email}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={ids.note}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.confirm} className="text-[15px] font-semibold">
            Email again
          </label>
          <input
            id={ids.confirm}
            type="email"
            name="email-confirm"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            className="field-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <p id={ids.note} className="text-[14px] text-muted">
          The code is tied to this email as soon as you continue, and can't be used with another one. Your results go to this email.
        </p>
        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? "Checking…" : "Continue"}
        </button>
        {error ? (
          <p role="alert" className="text-[15px] text-danger">
            {error}
          </p>
        ) : null}
        {used ? (
          <div role="alert" className="rounded-[16px] border border-line bg-panel p-4 text-[15px] leading-snug">
            <p>This code has already been used for a test.</p>
            <p className="mt-2">
              <Link to="/sign-in">Sign in with your email to see your results</Link>
            </p>
          </div>
        ) : null}
      </form>
    </Page>
  );
}
